import axios from 'axios';
import { refreshToken } from 'src/apis/login';
import { v4 as uuidv4 } from 'uuid';
import refreshTokenSemaphore from './Semaphore'; 

(() => {
    // allows cookies to be sent to the server
    axios.defaults.withCredentials = true;
})();

const TOKEN_EXPIRATION_TIME_KEY = '__token-expiration-time'

const tokenWasExpired = (tokenExpirationTime) => {
  return tokenExpirationTime && tokenExpirationTime.length && new Date(tokenExpirationTime) < new Date();
}

// Add a request interceptor
axios.interceptors.request.use(async (request) => {
    request.headers['X-Request-ID'] = uuidv4(); // store x-hash to every request
    // Do not add code-verifier to /auth request
    if (request.url.endsWith('/auth')) {
        return request;
    }
    /**
     * Store Code verifier after authentication to use on every authorized request.
     */
    const codeVerifier = localStorage.getItem('code-verifier');

    if (codeVerifier) {
      request.headers['code-verifier'] = codeVerifier;
    } 

    const currentPage = window.location.href
    if (currentPage.includes('__mock_login') || currentPage.includes('login') || request.url.includes('/auth/refresh')) {
      return request;
    }

    if (refreshTokenSemaphore.isLocked()) {
      await refreshTokenSemaphore.waitTillUnlockTokenValidation();
    }

    let tokenExpirationTime = localStorage.getItem(TOKEN_EXPIRATION_TIME_KEY);

    if (tokenWasExpired(tokenExpirationTime)) {
      refreshTokenSemaphore.lockTokenValidation();

      return refreshToken().then((response) => {    
        if (response?.data && response.data.result?.tokenExpirationTime) {
          localStorage.setItem(TOKEN_EXPIRATION_TIME_KEY, response.data.result?.tokenExpirationTime)
        }
        
        refreshTokenSemaphore.releaseLock()

        return request;
      })
      .catch((e) => {
        refreshTokenSemaphore.releaseLock();
        console.error(e);
        return Promise.reject(e);
      })
    } else {
      return request
    }
  },
  error => {
      Promise.reject(error)
  }
)

let requestInProgress = false;

axios.interceptors.response.use(
  response => {
    if (response.data && response.data.result?.tokenExpirationTime) {
      localStorage.setItem(TOKEN_EXPIRATION_TIME_KEY, response.data.result?.tokenExpirationTime)
    }

    return response
  },
  error => {
    if (error.response.status === 400 || error.response.status === 422) {
      throw error;
    }
    
    if (error.response.status === 403) {
      if (error.config.url.includes('/auth/refresh')) {
        refreshTokenSemaphore.unlockTokenValidation();
        localStorage.removeItem('code-verifier');
        window.location.href = '/unauthentication';
        throw error;
      }

      if (error.response.data.error === 'Access token has expired' && !requestInProgress) {
        requestInProgress = true;
        // refresh acess token:
        refreshToken().then(() => {
          // re-call previous failed request upon refreshing token:
          axios(error.config).catch(e => {
            console.error("Error on trying to recall refreshToken request", e);
            window.location.href = "/unauthentication";
          })
        }).catch(e => {
          console.error("Error on trying to refreshToken", e);
          window.location.href = "/unauthentication";
        })
      }

      console.log(error.response.data.message);
      if (error.response.data.message === 'Profile without access') {
        throw error
      }
    } else {
      refreshTokenSemaphore.unlockTokenValidation();

      localStorage.removeItem('code-verifier');
      
      if (error.response.status >= 500) {
        window.location.href = '/error';
      } else {
        window.location.href = '/unauthentication';
      }
      throw error;
    }
  }
);

export default axios;
